import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import CookieService from "../../context/cookies";
import {
  DealTarckerTables,
  UpdateTableOrderPayload,
} from "../../types/DealTracker";
import { base } from "../../constants/apis";

export const adminCustomerSheetsApi = createApi({
  reducerPath: "adminCustomerSheetsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${base}/apis/admin/customer_sheets`,
    prepareHeaders: (headers) => {
      const token = CookieService.getCookie("adminToken");

      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["adminCustomerSheets"],
  endpoints: (builder) => ({
    getAdminCustomerSheets: builder.query<DealTarckerTables, void>({
      query: () => "/get.php",
      providesTags: ["adminCustomerSheets"],
    }),
    getDeletedAdminCustomerSheets: builder.query<DealTarckerTables, void>({
      query: () => "/get_deleted_data.php",
      providesTags: ["adminCustomerSheets"],
      transformResponse: (res: DealTarckerTables) => ({
        ...res,
        data: res.data.reverse(),
      }),
    }),

    addAdminCustomerSheet: builder.mutation({
      query: (table) => ({
        url: "/add.php",
        method: "POST",
        body: table,
      }),
      invalidatesTags: ["adminCustomerSheets"],
      onQueryStarted: async (table, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          adminCustomerSheetsApi.util.updateQueryData(
            "getAdminCustomerSheets",
            undefined,
            (draft) => {
              const pinnedIndex = draft.data.findIndex((t) => t.is_pin === 1);
              const minOrder =
                draft.data.length > 0
                  ? Math.min(
                      ...draft.data.map((t) => t.table_order ?? Infinity),
                    )
                  : 0;

              // Create the new table object (optimistic temp ID)
              const newTable = {
                id: crypto.randomUUID(),
                table_order: minOrder - 1,
                ...table,
              };

              if (pinnedIndex !== -1) {
                // Insert right after the pinned table
                draft.data.splice(pinnedIndex + 1, 0, newTable);
              } else {
                // No pinned table → insert at top
                draft.data.unshift(newTable);
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patchResult.undo();
        }
      },
    }),
    updateAdminCustomerSheet: builder.mutation({
      query: (table) => ({ url: "/update.php", method: "POST", body: table }),
      invalidatesTags: ["adminCustomerSheets"],
      async onQueryStarted(
        { id, complete_json },
        { dispatch, queryFulfilled },
      ) {
        // optimistic patch
        const patch = dispatch(
          adminCustomerSheetsApi.util.updateQueryData(
            "getAdminCustomerSheets",
            undefined,
            (draft) => {
              const table = draft.data.find((t) => t.id === id);
              if (table) {
                table.complete_json = complete_json;
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo(); // rollback on failure
        }
      },
    }),
    // soft delete
    deleteAdminCustomerSheet: builder.mutation({
      query: (id) => ({ url: "/delete.php", method: "POST", body: { id } }),
      invalidatesTags: ["adminCustomerSheets"],
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          adminCustomerSheetsApi.util.updateQueryData(
            "getAdminCustomerSheets",
            undefined,
            (draft) => {
              const tableIndex = draft.data.findIndex((el) => el.id === id);
              draft.data.splice(tableIndex, 1);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patchResult.undo();
        }
      },
    }),

    updateAdminCustomerSheetTitle: builder.mutation({
      query: (payload) => ({
        url: "/update.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["adminCustomerSheets"],
      onQueryStarted: async ({ title, id }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          adminCustomerSheetsApi.util.updateQueryData(
            "getAdminCustomerSheets",
            undefined,
            (draft) => {
              const tableIndex = draft.data.findIndex((el) => el.id === id);
              draft.data[tableIndex].title = title;
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patchResult.undo();
        }
      },
    }),

    restoreAdminCustomerSheet: builder.mutation({
      query: (id) => ({ url: "/restore.php", method: "POST", body: { id } }),
      invalidatesTags: ["adminCustomerSheets"],
    }),

    forceDeleteAdminCustomerSheet: builder.mutation({
      query: (id) => ({
        url: "/force_delete.php",
        method: "POST",
        body: { id },
      }),
      invalidatesTags: ["adminCustomerSheets"],
    }),

    updateAdminCustomerSheetOrder: builder.mutation<
      void,
      UpdateTableOrderPayload
    >({
      query: (payload) => ({
        url: "/table_order.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["adminCustomerSheets"],

      async onQueryStarted(newOrder, { dispatch, queryFulfilled }) {
        // Optimistically update the order in cache
        const patchResult = dispatch(
          adminCustomerSheetsApi.util.updateQueryData(
            "getAdminCustomerSheets",
            undefined,
            (draft) => {
              draft.data.sort(
                (a, b) =>
                  (newOrder.find((o) => o.id === a.id)?.table_order ?? 0) -
                  (newOrder.find((o) => o.id === b.id)?.table_order ?? 0),
              );
              draft.data.forEach((t, i) => {
                t.table_order = i + 1;
              });
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo(); // rollback if failed
        }
      },
    }),

    updateCustomerSheetPin: builder.mutation<
      void,
      { id: string | number; is_pin: number }
    >({
      query: (payload) => ({
        url: `/pined_table.php`,
        method: "POST",
        body: payload,
      }),

      async onQueryStarted({ id, is_pin }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          adminCustomerSheetsApi.util.updateQueryData(
            "getAdminCustomerSheets",
            undefined,
            (draft) => {
              const clickedTable = draft.data.find((t) => t.id === id);
              if (!clickedTable) return;

              if (clickedTable.is_pin) {
                clickedTable.is_pin = is_pin;
                draft.data.sort((a, b) => a.table_order - b.table_order);
                return;
              }

              draft.data.forEach((t) => (t.is_pin = 0));
              clickedTable.is_pin = 1;

              // move it to the top
              draft.data = [
                clickedTable,
                ...draft.data
                  .filter((t) => t.id !== clickedTable.id)
                  .sort((a, b) => a.table_order - b.table_order),
              ];
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetAdminCustomerSheetsQuery,
  useAddAdminCustomerSheetMutation,
  useUpdateAdminCustomerSheetMutation,
  useDeleteAdminCustomerSheetMutation,
  useUpdateAdminCustomerSheetTitleMutation,
  useUpdateAdminCustomerSheetOrderMutation,

  useGetDeletedAdminCustomerSheetsQuery,
  useForceDeleteAdminCustomerSheetMutation,
  useRestoreAdminCustomerSheetMutation,

  useUpdateCustomerSheetPinMutation,
} = adminCustomerSheetsApi;
